import { useRef } from 'react';
import axios from 'axios';

export default function useGenerateAcquisitionsPdf() {
    const iframeHolder = useRef<HTMLIFrameElement>(null);
    const aTagHolder = useRef<HTMLAnchorElement>(null);
    const isRunning = useRef<boolean>(false);

    const initialiseGeneration = (dynamicPath: string) => {
        return new Promise(function (resolve) {
            if (iframeHolder.current === null) {
                iframeHolder.current = document.createElement('iframe');
                iframeHolder.current.style.display = 'None';
                iframeHolder.current.setAttribute('src', `${dynamicPath}.html`);
                document.body.appendChild(iframeHolder.current);
                iframeHolder.current.addEventListener('load', () => {
                    return resolve(iframeHolder.current.firstElementChild);
                });
            } else {
                resolve(iframeHolder.current.firstElementChild);
            }
        });

    };
    const fetchStaticBlob = async (staticPdf: string): Promise<Blob> => {
        const staticBlob = await axios.get(`/services/Pdf?pdfPath=${staticPdf}`, {
            method: 'GET',
            responseType: 'blob',
        })
            .then(res => new Blob([res.data], { type: 'application/pdf' }))
            .catch(err => console.error(err));

        return staticBlob || null;
    };

    const createDynamicContent = async (opt?: {
        margin?: number[],
        filename?: string
        image?: { type: 'jpeg' | 'png' | 'webp', quality: number },
        html2canvas?: { scale: number },
        jsPDF?: { unit: string, format: string, orientation: string, precision: number },
        pagebreak?: { mode: string },
    }): Promise<Blob> => {
        const html2pdf = (await import(/* webpackChunkName: "html2pdf" */ 'html2pdf.js')).default;
        return await html2pdf()
            .set({
                ...{
                    margin: [0.0, 0.05, 0.0, 0.05],
                    filename: 'test.pdf',
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 1 },
                    jsPDF: { unit: 'in', format: [12.5, 16.25], orientation: 'portrait', precision: 64, setFontSize: .7 },
                    pagebreak: { mode: 'legacy' },
                },
                ...opt,
            })
            .from(iframeHolder.current.contentWindow.document.body)
            .outputPdf('blob');
    };

    const joinPdf = async (FirstPdf: Blob, SecondPdf: Blob): Promise<string> => {
        const { PDFDocument } = await import(/* webpackChunkName: "pdf-lib" */ 'pdf-lib');
        const [pdf1, pdf2, mergedPdf] = await Promise.all([
            PDFDocument.load(await FirstPdf.arrayBuffer()),
            PDFDocument.load(await SecondPdf.arrayBuffer()),
            PDFDocument.create(),
        ]);
        const mergeHelper = (pageArray: any[]) => pageArray.forEach((page) => mergedPdf.addPage(page));
        await mergedPdf.copyPages(pdf1, pdf1.getPageIndices()).then(mergeHelper);
        await mergedPdf.copyPages(pdf2, pdf2.getPageIndices()).then(mergeHelper);

        return URL.createObjectURL(new Blob([await mergedPdf.save()], { type: 'application/pdf' }));
    };

    const generate = async (staticPath: string, dynamicPath: string, pdfOptions?: string) => {
        if (isRunning.current || !staticPath || !dynamicPath) {
            return;
        }

        const isPdfFile = dynamicPath.includes('.pdf');
        isRunning.current = true;
        if (!isPdfFile) {
            await initialiseGeneration(dynamicPath);
            if (!iframeHolder.current) {
                console.error('Unable to intialise iframe holder for pdf');
            }
        }

        let dynamicPdf: Blob;
        const staticPdf = await fetchStaticBlob(staticPath);
        if (isPdfFile) {
            dynamicPdf = await fetchStaticBlob(dynamicPath);
        } else {
            dynamicPdf = await createDynamicContent(pdfOptions ? JSON.parse(pdfOptions) : undefined);
        }

        if (!staticPdf || !dynamicPdf) {
            console.error('Unable to fetch blobs');
            return;
        }

        const pdfReference = await joinPdf(dynamicPdf, staticPdf);
        if (!pdfReference) {
            console.error('Unable to join PDF');
            return;
        }
        let pdfName;
        if (isPdfFile) {
            pdfName = dynamicPath.split('/').pop();
        } else {
            pdfName = dynamicPath.split('/').slice(-2, -1)[0] + '.pdf';
        }
        if (aTagHolder.current === null) {
            aTagHolder.current = document.createElement('a');
            aTagHolder.current.style.display = 'None';
        }
        aTagHolder.current.setAttribute('href', pdfReference);
        aTagHolder.current.setAttribute('download', pdfName);
        aTagHolder.current.click();

        isRunning.current = false;
    };

    return {
        generate,
    };
}
